/**
 * My Veda Coins — the whole coin journey in one honest place: what the student
 * earned, where it came from, where it went, and what it unlocks next.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { apiGet, apiPost } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TreasureChest } from '@/components/common/TreasureChest';
import ChestBurst from '@/components/common/ChestBurst';
import { HowToEarnDialog } from '@/components/study/HowToEarnDialog';
import {
  ArrowRight, CheckCircle2, Clock, Coins, FileText, Gift, HelpCircle,
  Loader2, Puzzle, ShieldAlert, Upload, Users, XCircle,
} from 'lucide-react';

const SECTION_ICONS = {
  referral: Users, syllabus_upload: Upload, question_bank: FileText, puzzle: Puzzle,
};
const SECTION_TINT = {
  referral: 'from-sky-50 to-white border-sky-100',
  syllabus_upload: 'from-amber-50 to-white border-amber-100',
  question_bank: 'from-violet-50 to-white border-violet-100',
  puzzle: 'from-emerald-50 to-white border-emerald-100',
};
const STATUS = {
  submitted: { label: 'Submitted', cls: 'bg-slate-100 text-slate-700', Icon: Clock },
  under_review: { label: 'Under review', cls: 'bg-amber-100 text-amber-800', Icon: Clock },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
  rejected: { label: 'Rejected', cls: 'bg-rose-100 text-rose-800', Icon: XCircle },
};

const coinFmt = n => Number(n || 0).toLocaleString('en-IN');
const when = s => (s ? new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '');

export default function MyVedaCoinsPage() {
  const [data, setData] = useState(null);
  const [contrib, setContrib] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [howOpen, setHowOpen] = useState(false);
  const [burst, setBurst] = useState({ open: false, coins: 0 });
  const [busy, setBusy] = useState(0);

  const load = useCallback(async () => {
    const [wallet, list, board] = await Promise.all([
      apiGet('/coins/me'),
      apiGet('/coins/contributions').catch(() => ({ items: [] })),
      apiGet('/coins/leaderboard').catch(() => ({ entries: [] })),
    ]);
    setData(wallet);
    setContrib(list.items || []);
    setLeaders(board.entries || []);
  }, []);

  useEffect(() => {
    load().catch(() => toast.error('Could not load your Veda Coins just now'));
  }, [load]);

  // The chest stays restless until the child opens it, and only rests again
  // when the next coin lands inside.
  const openChest = async () => {
    try {
      const res = await apiPost('/coins/chest-opened', {});
      window.dispatchEvent(new Event('gv:chest-opened'));
      if ((res.fresh || 0) > 0) {
        setBurst({ open: true, coins: res.fresh });
      } else {
        toast.message('Nothing new in the chest yet — earn a few and come back!');
      }
      await load();
    } catch { toast.error('The chest is stuck — try again in a moment'); }
  };

  const redeem = async (months) => {
    setBusy(months);
    try {
      const res = await apiPost('/coins/redeem-premium', { months });
      toast.success(`${res.months} months of Premium unlocked with your coins!`);
      await load();
    } catch (e) { toast.error(e?.message || 'Could not redeem just yet'); }
    setBusy(0);
  };

  if (!data) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const nextMilestone = data.milestones.find(m => !m.reached);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="my-veda-coins-page">
      {/* Treasure header */}
      <Card className="rounded-3xl border-amber-200 bg-gradient-to-br from-amber-50 via-orange-50 to-white p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="flex items-center gap-4">
            <button data-testid="open-chest-btn" onClick={openChest}
              title={data.chest?.fresh > 0 ? 'Tap to open your chest' : 'Your chest is already open'}
              className="w-16 h-16 rounded-2xl bg-amber-500/15 text-amber-600 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform">
              <TreasureChest className="w-9 h-9" shake={data.chest?.fresh > 0} />
            </button>
            <div>
              <p className="text-[11px] font-heading uppercase tracking-widest text-amber-700">Your treasure</p>
              <p className="font-heading text-4xl font-extrabold text-slate-900 leading-none" data-testid="coins-total">
                {coinFmt(data.balance)}
              </p>
              {data.chest?.fresh > 0 ? (
                <button onClick={openChest} data-testid="chest-fresh-hint"
                  className="text-[12px] font-heading text-amber-700 mt-0.5 hover:underline">
                  {coinFmt(data.chest.fresh)} new coins waiting — tap the chest!
                </button>
              ) : (
                <p className="text-[12px] font-body text-slate-500 mt-0.5">Veda Coins in the chest</p>
              )}
            </div>
          </div>
          <div className="sm:ml-auto flex gap-6">
            <div>
              <p className="text-[11px] font-body text-slate-500">Earned so far</p>
              <p className="font-heading text-lg text-emerald-700" data-testid="coins-earned-total">+{coinFmt(data.earned_total)}</p>
            </div>
            <div>
              <p className="text-[11px] font-body text-slate-500">Spent so far</p>
              <p className="font-heading text-lg text-rose-700" data-testid="coins-spent-total">−{coinFmt(data.spent_total)}</p>
            </div>
          </div>
        </div>
        {nextMilestone && (
          <div className="mt-5">
            <div className="flex items-center justify-between text-[12px] font-body text-slate-600">
              <span>Next up: <span className="font-heading text-slate-900">{nextMilestone.label}</span></span>
              <span data-testid="next-milestone-remaining">{coinFmt(nextMilestone.remaining)} coins to go</span>
            </div>
            <div className="h-2 rounded-full bg-amber-100 mt-1.5 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-700"
                style={{ width: `${nextMilestone.progress}%` }} />
            </div>
          </div>
        )}
      </Card>

      {/* Earning sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.sections.map(s => {
          const Icon = SECTION_ICONS[s.key] || Coins;
          return (
            <Card key={s.key} data-testid={`coins-section-${s.key}`}
              className={`rounded-2xl p-5 bg-gradient-to-br ${SECTION_TINT[s.key] || 'from-slate-50 to-white border-slate-100'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-slate-700" />
                  <p className="font-heading text-[15px] font-bold text-slate-900">{s.title}</p>
                </div>
                <Badge variant="secondary" className="font-heading text-[10px]">
                  {s.coins} coins {s.unit}
                </Badge>
              </div>
              <p className="font-heading text-2xl font-extrabold text-slate-900 mt-3" data-testid={`coins-section-total-${s.key}`}>
                +{coinFmt(s.earned)}
              </p>
              <p className="text-[11px] font-body text-slate-500">
                from {s.count} {s.count === 1 ? 'contribution' : 'contributions'}
              </p>
              {/* The journey, in the student's words */}
              <div className="flex flex-wrap items-center gap-1 mt-3">
                {s.journey.map((step, i) => (
                  <span key={i} className="inline-flex items-center gap-1">
                    <span className="px-2 py-0.5 rounded-full bg-white/80 border border-slate-200 text-[10px] font-heading text-slate-600">
                      {step}
                    </span>
                    {i < s.journey.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300" />}
                  </span>
                ))}
              </div>
              {s.key === 'syllabus_upload' && (
                <button type="button" data-testid="coins-how-link" onClick={() => setHowOpen(true)}
                  className="mt-3 inline-flex items-center gap-1 text-[11px] font-heading text-amber-700 hover:underline">
                  <HelpCircle className="w-3 h-3" /> Click here to know how
                </button>
              )}
            </Card>
          );
        })}
      </div>

      {/* Contribution status */}
      <Card className="rounded-2xl p-5 border-slate-100" data-testid="coins-contributions-card">
        <p className="font-heading text-[15px] font-bold text-slate-900">Your contributions</p>
        <p className="text-[11px] font-body text-slate-500">
          Submitted → Under review → Approved. Coins arrive only after approval, once per chapter.
        </p>
        <div className="mt-3 space-y-2">
          {contrib.map(c => {
            const st = STATUS[c.status] || STATUS.submitted;
            return (
              <div key={`${c.kind}-${c.id}`} data-testid={`contribution-${c.id}`}
                className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-100 p-3">
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-[13px] text-slate-900 truncate">
                    {c.title} {c.chapter_count ? <span className="text-slate-400 font-body">· {c.chapter_count} chapters</span> : null}
                  </p>
                  <p className="text-[11px] font-body text-slate-500 truncate">
                    {c.detail} {c.submitted_at ? `· sent ${when(c.submitted_at)}` : ''}
                  </p>
                  {c.reason && (
                    <p className="text-[11px] font-body text-rose-700 mt-1" data-testid={`contribution-reason-${c.id}`}>
                      Reviewer’s note: {c.reason}
                    </p>
                  )}
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-heading ${st.cls}`}
                  data-testid={`contribution-status-${c.id}`}>
                  <st.Icon className="w-3 h-3" /> {st.label}
                </span>
              </div>
            );
          })}
          {!contrib.length && (
            <p className="text-xs font-body text-slate-400" data-testid="contributions-empty">
              Nothing submitted yet. Upload a chapter and it will show up here within seconds.
            </p>
          )}
        </div>
      </Card>

      {/* Reimbursement ladder */}
      <Card className="rounded-2xl p-5 border-slate-100" data-testid="coins-milestones-card">
        <p className="font-heading text-[15px] font-bold text-slate-900 flex items-center gap-2">
          <Gift className="w-4 h-4 text-violet-500" /> Reimbursement
        </p>
        <p className="text-[11px] font-body text-slate-500">
          Normal theme → Special theme → 3 months Premium → 6 months Premium. Aim high.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          {data.milestones.map(m => {
            const redeemable = data.redemptions.find(r => `premium_${r.months}m` === m.key);
            return (
              <div key={m.key} data-testid={`milestone-${m.key}`}
                className={`rounded-2xl border p-4 ${m.reached ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200'}`}>
                <p className="font-heading text-[13px] text-slate-900">{m.label}</p>
                <p className="font-heading text-xl font-extrabold text-slate-900 mt-1">{coinFmt(m.coins)}</p>
                <p className="text-[11px] font-body text-slate-500 leading-snug mt-0.5">{m.note}</p>
                <div className="h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden">
                  <div className={`h-full rounded-full ${m.reached ? 'bg-emerald-500' : 'bg-violet-400'}`}
                    style={{ width: `${m.progress}%` }} />
                </div>
                {m.reached ? (
                  redeemable ? (
                    <Button size="sm" className="w-full mt-3 rounded-full font-heading text-[11px]"
                      data-testid={`redeem-${m.key}`} disabled={!!busy}
                      onClick={() => redeem(redeemable.months)}>
                      {busy === redeemable.months && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                      Redeem now
                    </Button>
                  ) : (
                    <p className="text-[11px] font-heading text-emerald-700 mt-3">Ready — pick it in Study theme</p>
                  )
                ) : (
                  <p className="text-[11px] font-body text-slate-400 mt-3">{coinFmt(m.remaining)} coins to go</p>
                )}
              </div>
            );
          })}
        </div>
        {data.premium_until && (
          <p className="text-[11px] font-body text-emerald-700 mt-3" data-testid="coin-premium-until">
            Your coin-funded Premium runs until {new Date(data.premium_until).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}.
          </p>
        )}
      </Card>

      {/* Monthly contributor board */}
      {!!leaders.length && (
        <Card className="rounded-2xl p-5 border-slate-100" data-testid="coins-leaderboard-card">
          <p className="font-heading text-[15px] font-bold text-slate-900">Top contributors this month</p>
          <p className="text-[11px] font-body text-slate-500">
            Chapters, question papers and referrals — the students helping everyone else learn.
          </p>
          <div className="mt-3 space-y-1.5">
            {leaders.map(l => (
              <div key={l.rank} data-testid={`leader-row-${l.rank}`}
                className={`flex items-center gap-3 rounded-xl px-3 py-2 ${l.is_me ? 'bg-amber-50 ring-1 ring-amber-200' : 'bg-slate-50'}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center font-heading text-[11px] ${l.rank === 1 ? 'bg-amber-400 text-white' : l.rank === 2 ? 'bg-slate-300 text-white' : l.rank === 3 ? 'bg-orange-300 text-white' : 'bg-white text-slate-500 border border-slate-200'}`}>
                  {l.rank}
                </span>
                <span className="font-heading text-[13px] text-slate-800 truncate flex-1">
                  {l.name}{l.class_level ? <span className="text-slate-400 font-body"> · Class {l.class_level}</span> : null}
                  {l.is_me ? <span className="text-amber-700 font-body"> · you</span> : null}
                </span>
                <span className="text-[11px] font-body text-slate-400">{l.items} items</span>
                <span className="font-heading text-[13px] text-amber-700">{coinFmt(l.coins)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Usage / where the coins went */}
      <Card className="rounded-2xl p-5 border-slate-100" data-testid="coins-usage-card">
        <p className="font-heading text-[15px] font-bold text-slate-900">Usage</p>
        <p className="text-[11px] font-body text-slate-500">
          Every coin that leaves the chest — hints cost 5 coins each, themes and Premium cost more.
        </p>
        <div className="mt-3 divide-y divide-slate-100">
          {data.usage.map((u, i) => (
            <div key={i} className="flex items-center justify-between py-2" data-testid={`usage-row-${i}`}>
              <div className="min-w-0">
                <p className="font-heading text-[12px] text-slate-800 truncate">{u.label}</p>
                <p className="text-[10px] font-body text-slate-400">{when(u.at)}</p>
              </div>
              <span className="font-heading text-[13px] text-rose-700">−{coinFmt(u.coins)}</span>
            </div>
          ))}
          {!data.usage.length && (
            <p className="text-xs font-body text-slate-400 py-2" data-testid="usage-empty">
              Nothing spent yet — your chest is untouched.
            </p>
          )}
        </div>
      </Card>

      <p className="text-[11px] font-body text-slate-400 flex items-start gap-1.5" data-testid="coins-fairness-note">
        <ShieldAlert className="w-3.5 h-3.5 mt-[1px] flex-shrink-0" />
        Coins are a thank-you for genuine contributions. Duplicate, unreadable or ineligible uploads
        don’t qualify, and coins awarded for a submission later found ineligible may be withdrawn.
      </p>

      <HowToEarnDialog open={howOpen} onOpenChange={setHowOpen} />
      <ChestBurst open={burst.open} coins={burst.coins}
        onClose={() => setBurst({ open: false, coins: 0 })} />
    </div>
  );
}
